import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Loader2, ChevronLeft, ChevronRight } from 'lucide-react'
import { api } from '@/lib/api'
import { useAuth } from '@/hooks/use-auth'
import { buildAppHref } from '@/lib/auth'
import { formatDuration, formatEpoch, entityRefClass } from '@/lib/format'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

function formatMacFromNumber(num: number): string {
  const hex = num.toString(16).toUpperCase().padStart(12, '0')
  return hex.match(/.{1,2}/g)!.join(':')
}

export function AdminTravelTimesPage() {
  const auth = useAuth()
  const [page, setPage] = useState(1)
  const [pageSize] = useState(50)
  const [linkIdFilter, setLinkIdFilter] = useState('')
  const [startTimestamp, setStartTimestamp] = useState('')
  const [endTimestamp, setEndTimestamp] = useState('')

  const buildParams = () => {
    const params: Record<string, string> = { page: String(page), page_size: String(pageSize) }
    if (linkIdFilter) params.link_id = linkIdFilter
    if (startTimestamp) params.start_timestamp = startTimestamp
    if (endTimestamp) params.end_timestamp = endTimestamp
    return params
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-travel-times', page, pageSize, linkIdFilter, startTimestamp, endTimestamp],
    queryFn: () => api.adminListTravelTimes(buildParams()),
  })

  const totalPages = data ? Math.ceil(data.total / pageSize) : 0

  return (
    <Card className="bg-card/95 backdrop-blur-sm">
      <CardHeader>
        <CardTitle className="text-2xl">Travel Times</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <div>
            <Label className="mb-1 block text-xs text-muted-foreground">Link ID</Label>
            <Input
              type="number"
              placeholder="Filter by link ID"
              className="w-40"
              value={linkIdFilter}
              onChange={(e) => { setLinkIdFilter(e.target.value); setPage(1) }}
            />
          </div>
          <div>
            <Label className="mb-1 block text-xs text-muted-foreground">Start Timestamp</Label>
            <Input
              type="number"
              placeholder="From"
              className="w-44"
              value={startTimestamp}
              onChange={(e) => { setStartTimestamp(e.target.value); setPage(1) }}
            />
          </div>
          <div>
            <Label className="mb-1 block text-xs text-muted-foreground">End Timestamp</Label>
            <Input
              type="number"
              placeholder="To"
              className="w-44"
              value={endTimestamp}
              onChange={(e) => { setEndTimestamp(e.target.value); setPage(1) }}
            />
          </div>
        </div>

        {isLoading && (
          <div className="py-12 text-center text-muted-foreground">
            <Loader2 className="inline h-5 w-5 animate-spin mr-2" /> Loading travel times...
          </div>
        )}

        {error && (
          <div className="rounded-lg border-l-4 border-destructive bg-destructive/10 p-4 text-destructive">
            Error loading travel times: {(error as Error).message}
          </div>
        )}

        {data && (
          <>
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-primary hover:bg-primary">
                    <TableHead className="text-primary-foreground font-semibold">Link ID</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Mac ID</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Finish Timestamp</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Duration (s)</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Avg Duration</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Base Duration</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground">No travel times found</TableCell>
                    </TableRow>
                  ) : (
                    data.items.map((tt, i) => (
                      <TableRow key={`${tt.link_id}-${tt.mac_id}-${tt.finish_timestamp}-${i}`}>
                        <TableCell className="font-medium">
                          <Link to={buildAppHref(`/admin/links/${tt.link_id}`, auth)} className={entityRefClass}>{tt.link_id}</Link>
                        </TableCell>
                        <TableCell className="font-mono text-xs">{formatMacFromNumber(tt.mac_id)}</TableCell>
                        <TableCell>{formatEpoch(tt.finish_timestamp)}</TableCell>
                        <TableCell>
                          {tt.duration_seconds == null ? '—' : tt.duration_seconds === 999999 ? (
                            <span className="text-muted-foreground italic">auto</span>
                          ) : formatDuration(tt.duration_seconds)}
                        </TableCell>
                        <TableCell>{formatDuration(tt.average_duration)}</TableCell>
                        <TableCell>{formatDuration(tt.base_duration)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Page {data.page} of {totalPages} ({data.total} total)
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
